import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { ChevronLeft, DollarSign } from 'lucide-react';
import { PRODUCTOS } from '../constants/productos';
import { fmtMoney } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Precios() {
  const { user } = useAuth();
  const { cacheData, getCachedData } = useOffline();
  const navigate = useNavigate();
  
  const [precios, setPrecios] = useState({});
  const [pesoProm, setPesoProm] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPrecios();
  }, []);

  const loadPrecios = async () => {
    try {
      const { data } = await axios.get(`${API}/precios/${user?.punto_venta_id}`);
      setPrecios(data.precios || {});
      setPesoProm(data.peso_promedio || 0);
      cacheData(`precios_${user?.punto_venta_id}`, data);
    } catch (error) {
      const cached = getCachedData(`precios_${user?.punto_venta_id}`);
      if (cached) {
        setPrecios(cached.precios || {});
        setPesoProm(cached.peso_promedio || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const fecha = new Date().toLocaleDateString('es-MX', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="precios-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900">Lista de Precios</h1>
        </div>
      </div>
      
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <p className="text-zinc-500 capitalize">{fecha}</p>
          {pesoProm > 0 && (
            <p className="text-sm text-zinc-400 mt-1">Factor peso promedio: {pesoProm} kg</p>
          )}
        </div>

        {/* Price List */}
        <div className="space-y-1">
          {PRODUCTOS.map((producto) => {
            const precio = precios[producto.clave] || 0;
            return (
              <div
                key={producto.clave}
                className="flex items-center justify-between py-4 border-b border-zinc-100"
                data-testid={`precio-${producto.clave}`}
              >
                <span className="text-lg font-medium text-zinc-900">{producto.nombre}</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-zinc-900">
                    {precio > 0 ? fmtMoney(precio) : '--'}
                  </span>
                  <span className="text-zinc-400 text-sm"> / pieza</span>
                </div>
              </div>
            );
          })}
        </div>

        {Object.values(precios).every(p => !p || p === 0) && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
            <p className="text-yellow-800">
              Los precios aún no han sido configurados para hoy
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
